import { Lock } from "lucide-react";
import { useState, type FormEvent } from "react";

import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DayPicker } from "@/components/groups/day-chips";
import { TownCombobox } from "@/components/groups/town-combobox";
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/hooks/use-auth";
import { useMyGroups } from "@/hooks/use-groups";
import { t } from "@/i18n/pl";
import { formatDays } from "@/lib/format";
import { myDays } from "@/lib/groups";
import * as groupsService from "@/services/groups.service";
import type { GroupCreateInput, GroupDetail, PickupArea } from "@/types/api";

/**
 * The one group form, shared by "Załóż grupę" and "Ustawienia grupy" so the
 * two screens can never drift apart. Pass `initial` to prefill for editing.
 */
export function GroupForm({
  initial,
  pending,
  submitLabel,
  onSubmit,
}: {
  initial?: GroupDetail;
  pending: boolean;
  submitLabel: string;
  onSubmit: (input: GroupCreateInput) => void;
}) {
  const { data: plants } = useQuery({
    queryKey: ["plants"],
    queryFn: groupsService.listPlants,
    staleTime: Infinity,
  });

  const [carModel, setCarModel] = useState(initial?.car_model ?? "");
  const [seats, setSeats] = useState(String(initial?.seats_total ?? 3));
  const [area, setArea] = useState<PickupArea | null>(initial?.pickup_area ?? null);
  const [plantId, setPlantId] = useState(initial?.plant.id ?? "");
  const [pickupDetails, setPickupDetails] = useState(initial?.pickup_details ?? "");
  const [departureTime, setDepartureTime] = useState(
    initial?.departure_time.slice(0, 5) ?? "05:00",
  );
  const [returnTime, setReturnTime] = useState(initial?.return_time.slice(0, 5) ?? "13:30");
  const [days, setDays] = useState<number[]>(initial?.days ?? [0, 1, 2, 3, 4]);

  // Nobody drives and rides on the same weekday, nor drives two of their own
  // groups on one (the API refuses both): the days this person already holds
  // a seat on, pending or active, and the days of their other groups are
  // shown struck through and dropped from whatever is selected. The rule is
  // about the car's DRIVER — an admin editing someone else's group must not
  // have their own days applied to that car's schedule.
  const { user } = useAuth();
  const { data: myGroups } = useMyGroups();
  const viewerIsDriver = !initial || initial.driver.id === user?.id;
  const busyDays = viewerIsDriver
    ? (myGroups ?? [])
        .filter((group) => group.id !== initial?.id)
        .flatMap((group) => myDays(group, user?.id))
    : [];
  const drivingDays = days.filter((day) => !busyDays.includes(day));

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    // Both are deliberate choices, never defaults: the town anchors the route
    // corridor and there are two work sites people drive to.
    if (!area || !plantId || drivingDays.length === 0) return;
    onSubmit({
      car_model: carModel,
      seats_total: Number(seats),
      pickup_area_id: area.id,
      plant_id: plantId,
      pickup_details: pickupDetails.trim() || null,
      departure_time: `${departureTime}:00`,
      return_time: `${returnTime}:00`,
      days: drivingDays,
    });
  }

  return (
    <form onSubmit={handleSubmit}>
      <FieldGroup>
        <Field>
          <FieldLabel htmlFor="car-model">{t.createGroup.carModel}</FieldLabel>
          <Input
            id="car-model"
            required
            maxLength={80}
            placeholder={t.createGroup.carModelPlaceholder}
            value={carModel}
            onChange={(event) => setCarModel(event.target.value)}
          />
        </Field>

        <Field>
          <FieldLabel htmlFor="seats">{t.createGroup.seats}</FieldLabel>
          <Select value={seats} onValueChange={setSeats}>
            <SelectTrigger id="seats" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field>
          <FieldLabel htmlFor="area">{t.createGroup.area}</FieldLabel>
          <TownCombobox id="area" value={area} onSelect={setArea} />
          <FieldDescription>{t.createGroup.areaHint}</FieldDescription>
        </Field>

        <Field>
          <FieldLabel htmlFor="plant">{t.createGroup.plant}</FieldLabel>
          <Select value={plantId} onValueChange={setPlantId}>
            <SelectTrigger id="plant" className="w-full">
              <SelectValue placeholder={t.createGroup.plantPlaceholder} />
            </SelectTrigger>
            <SelectContent>
              {(plants ?? []).map((plant) => (
                <SelectItem key={plant.id} value={plant.id}>
                  {plant.name}
                  <span className="text-muted-foreground ml-1 text-xs">{plant.address}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field>
          <FieldLabel htmlFor="pickup-details">{t.createGroup.pickupDetails}</FieldLabel>
          <Input
            id="pickup-details"
            maxLength={300}
            placeholder={t.createGroup.pickupDetailsPlaceholder}
            value={pickupDetails}
            onChange={(event) => setPickupDetails(event.target.value)}
          />
          <FieldDescription className="flex items-center gap-1">
            <Lock className="size-3" aria-hidden /> {t.createGroup.pickupDetailsHint}
          </FieldDescription>
        </Field>

        <Field>
          <FieldLabel>{t.days.drivingDays}</FieldLabel>
          <DayPicker
            allowed={[0, 1, 2, 3, 4, 5, 6]}
            selected={drivingDays}
            onChange={setDays}
            disabled={busyDays}
          />
          {busyDays.length > 0 && (
            <FieldDescription>{t.createGroup.daysBusyHint(formatDays(busyDays))}</FieldDescription>
          )}
        </Field>

        {/* Stacked on phones: the native time control needs the full width so
            the browser's picker is not squeezed into half a column. */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field>
            <FieldLabel htmlFor="departure">{t.createGroup.departureTime}</FieldLabel>
            <Input
              id="departure"
              type="time"
              required
              value={departureTime}
              onChange={(event) => setDepartureTime(event.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="return">{t.createGroup.returnTime}</FieldLabel>
            <Input
              id="return"
              type="time"
              required
              value={returnTime}
              onChange={(event) => setReturnTime(event.target.value)}
            />
          </Field>
        </div>

        <Button
          type="submit"
          className="h-11 font-semibold"
          disabled={pending || !area || !plantId || drivingDays.length === 0}
        >
          {submitLabel}
        </Button>
      </FieldGroup>
    </form>
  );
}
